import {
  useMutation,
  useQueryClient,
  QueryClient,
} from '@tanstack/react-query';

import { addonAccessUpdate } from '@api/sdk.gen';
import { AddonsAddonListResponse } from '@api/types.gen';

import { withError } from '@/react-tools/react-query';

import { queryKeys } from '../query-keys';

// Addon access is a flat allow-list: the gateway only checks for presence in
// these maps (see AuthorizedAddonAccess in api/http/security/authorization.go),
// so RoleId carries no meaning.
const NO_ROLE = 0;

interface UpdateTeamAddonAccessPayload {
  teamId: number;
  /** The full set of add-ons this team should have access to. */
  addonIds: string[];
}

export function useUpdateTeamAddonAccessMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateTeamAddonAccessPayload) =>
      updateTeamAddonAccess(queryClient, payload),
    // The detail query also needs invalidating for each updated add-on: the
    // access tab seeds its form from it, so without this the saved grants
    // would read back as stale (see useUpdateAddonAccess).
    onSuccess: (updatedAddonIds) =>
      Promise.all(
        [queryKeys.list(), ...updatedAddonIds.map(queryKeys.detail)].map(
          (keys) => queryClient.invalidateQueries(keys)
        )
      ),
    ...withError("Unable to update the team's add-on access"),
  });
}

// Diffs the desired add-on set against each enabled add-on's current grants
// and only updates the ones that actually changed, merging into (rather than
// replacing) each add-on's existing team grants so other teams keep theirs.
// Disabled add-ons (or ones with grants staged before install) are left
// untouched, since they aren't offered in the select the payload came from.
async function updateTeamAddonAccess(
  queryClient: QueryClient,
  { teamId, addonIds }: UpdateTeamAddonAccessPayload
) {
  const addons =
    queryClient.getQueryData<AddonsAddonListResponse>(queryKeys.list())
      ?.addons ?? [];
  const desiredAddonIds = new Set(addonIds);

  const updatedAddonIds: string[] = [];
  await Promise.all(
    addons
      .filter((addon) => addon.enabled)
      .map((addon) => {
        const addonId = addon.id;
        if (!addonId) {
          return undefined;
        }

        const hasAccess = Boolean(addon.record?.teamAccessPolicies?.[teamId]);
        const wantsAccess = desiredAddonIds.has(addonId);
        if (hasAccess === wantsAccess) {
          return undefined;
        }

        const teamAccessPolicies = { ...addon.record?.teamAccessPolicies };
        if (wantsAccess) {
          teamAccessPolicies[teamId] = { RoleId: NO_ROLE };
        } else {
          delete teamAccessPolicies[teamId];
        }

        updatedAddonIds.push(addonId);
        return addonAccessUpdate({
          path: { id: addonId },
          body: { teamAccessPolicies },
        });
      })
  );

  return updatedAddonIds;
}
